// frontend/src/api/variants.js
import client from "./client";

/**
 * A/B testing variant API — wrappers around the /api/codes/{id}/variants
 * endpoints (P14-G5, Pro+ feature).
 */

/**
 * List a code's A/B variants (empty array = no test running).
 * @param {number} codeId
 * @returns {Promise<Array<{id, name, destination, weight, scan_count}>>}
 */
export async function listVariants(codeId) {
  const response = await client.get(`/api/codes/${codeId}/variants`);
  return response.data;
}

/**
 * Replace a code's variant set atomically (Pro+; weights must sum to 100).
 * @param {number} codeId
 * @param {Array<{ name: string, destination: string, weight: number }>} variants
 * @returns {Promise<Array<object>>}
 */
export async function setVariants(codeId, variants) {
  const response = await client.post(`/api/codes/${codeId}/variants`, {
    variants,
  });
  return response.data;
}

/**
 * Update one variant (weight changes must keep the code's total at 100).
 * @param {number} codeId
 * @param {string} variantId
 * @param {{ name?: string, destination?: string, weight?: number }} payload
 * @returns {Promise<object>}
 */
export async function updateVariant(codeId, variantId, payload) {
  const response = await client.patch(
    `/api/codes/${codeId}/variants/${variantId}`,
    payload,
  );
  return response.data;
}

/**
 * Delete one variant (A/B routing pauses if weights no longer sum to 100).
 * @param {number} codeId
 * @param {string} variantId
 * @returns {Promise<void>}
 */
export async function deleteVariant(codeId, variantId) {
  await client.delete(`/api/codes/${codeId}/variants/${variantId}`);
}

/**
 * Fetch per-variant scan counts, percentages, and chi-squared significance.
 * @param {number} codeId
 * @returns {Promise<{variants, total_scans, chi_squared, degrees_of_freedom, significant}>}
 */
export async function getABAnalytics(codeId) {
  const response = await client.get(`/api/codes/${codeId}/analytics/ab`);
  return response.data;
}
